import type { ChatDoneEvent, ChatMessage } from '@/types/chat'
import type { MaterialQueryResponse } from '@/types/material'

const RAW_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined
export const API_BASE = (RAW_BASE || '/api').replace(/\/+$/, '')

export interface StreamHandlers {
  signal?: AbortSignal
  onToken: (text: string) => void
  onDone?: (event: ChatDoneEvent) => void
}

export class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function toError(res: Response) {
  const text = await res.text().catch(() => '')
  let message = text
  try {
    const parsed = JSON.parse(text) as { error?: string }
    if (parsed.error) message = parsed.error
  } catch {
    // 非 JSON 错误体，原样展示
  }
  return new ApiError(message || `请求失败：${res.status} ${res.statusText}`, res.status)
}

export function request(path: string, init?: RequestInit) {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  })
}

export interface HealthInfo {
  status: string
  model?: string
  memory?: string
}

export async function getHealth() {
  const res = await request('/health')
  if (!res.ok) throw await toError(res)
  return (await res.json()) as HealthInfo
}

/** 物料判定：后端两层规则引擎（取料 + 提炼），无料时如实返回无法判定 */
export async function queryMaterial(text: string, signal?: AbortSignal) {
  const res = await request('/query', { method: 'POST', body: JSON.stringify({ text }), signal })
  if (!res.ok) throw await toError(res)
  return (await res.json()) as MaterialQueryResponse
}

export async function sendChat(input: { messages: ChatMessage[]; conversationId?: string }) {  const res = await request('/chat', {
    method: 'POST',
    body: JSON.stringify({
      conversationId: input.conversationId,
      messages: input.messages.map(({ role, content }) => ({ role, content })),
    }),
  })
  if (!res.ok) throw await toError(res)
  return (await res.json()) as ChatDoneEvent & { answer: string }
}

/** Consume the Worker's SSE stream: `event: token|done|error` + `data: <json>`. */
export async function streamChat(
  input: { messages: ChatMessage[]; conversationId?: string },
  handlers: StreamHandlers,
) {
  const res = await fetch(`${API_BASE}/chat/stream`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify({
      conversationId: input.conversationId,
      messages: input.messages.map(({ role, content }) => ({ role, content })),
    }),
    signal: handlers.signal,
  })
  if (!res.ok || !res.body) throw await toError(res)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const dispatch = (block: string) => {
    const event = /^event:\s*(.+)$/m.exec(block)?.[1]?.trim() ?? 'message'
    const data = block
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trim())
      .join('\n')
    if (!data) return

    const payload = JSON.parse(data) as { text?: string; message?: string } & ChatDoneEvent
    if (event === 'token') handlers.onToken(payload.text ?? '')
    if (event === 'done') handlers.onDone?.(payload)
    if (event === 'error') throw new ApiError(payload.message ?? '模型调用失败', 500)
  }

  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep = buffer.indexOf('\n\n')
    while (sep !== -1) {
      dispatch(buffer.slice(0, sep))
      buffer = buffer.slice(sep + 2)
      sep = buffer.indexOf('\n\n')
    }
  }
  if (buffer.trim()) dispatch(buffer)
}
